// User Management Store - SAT V4 Zone
import { create } from 'zustand';
import type { Teacher, Pupil, ParentCarer, Class, YearGroup } from '../types';
import { mockTeachers, mockPupils, mockParentCarers, mockClasses } from '../lib/mockData';

interface UserManagementState {
  // Data
  teachers: Teacher[];
  pupils: Pupil[];
  parentCarers: ParentCarer[];
  classes: Class[];

  // Selection state
  selectedTeacherId: string | null;
  selectedPupilId: string | null;

  // Filters
  teacherSearchQuery: string;
  pupilSearchQuery: string;
  selectedYearGroup: YearGroup | null;

  // Actions - Teachers
  setSelectedTeacherId: (id: string | null) => void;
  addTeacher: (teacher: Teacher) => void;
  updateTeacher: (id: string, updates: Partial<Teacher>) => void;
  deleteTeacher: (id: string) => void;
  setTeacherSearchQuery: (query: string) => void;

  // Actions - Pupils
  setSelectedPupilId: (id: string | null) => void;
  addPupil: (pupil: Pupil) => void;
  updatePupil: (id: string, updates: Partial<Pupil>) => void;
  deletePupil: (id: string) => void;
  setPupilSearchQuery: (query: string) => void;

  // Actions - General
  setSelectedYearGroup: (yearGroup: YearGroup | null) => void;

  // Getters
  getTeacherById: (id: string) => Teacher | undefined;
  getPupilById: (id: string) => Pupil | undefined;
  getParentsByPupilId: (pupilId: string) => ParentCarer[];
  getPupilsByTeacherId: (teacherId: string) => Pupil[];
  getFilteredTeachers: () => Teacher[];
  getFilteredPupils: () => Pupil[];
}

export const useUserManagementStore = create<UserManagementState>((set, get) => ({
  // Initialize with mock data
  teachers: mockTeachers,
  pupils: mockPupils,
  parentCarers: mockParentCarers,
  classes: mockClasses,

  selectedTeacherId: null,
  selectedPupilId: null,
  teacherSearchQuery: '',
  pupilSearchQuery: '',
  selectedYearGroup: null,

  // Teacher actions
  setSelectedTeacherId: (id) => set({ selectedTeacherId: id }),

  addTeacher: (teacher) =>
    set((state) => ({
      teachers: [...state.teachers, teacher],
    })),

  updateTeacher: (id, updates) =>
    set((state) => ({
      teachers: state.teachers.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    })),

  deleteTeacher: (id) =>
    set((state) => ({
      teachers: state.teachers.filter((t) => t.id !== id),
    })),

  setTeacherSearchQuery: (query) => set({ teacherSearchQuery: query }),

  // Pupil actions
  setSelectedPupilId: (id) => set({ selectedPupilId: id }),

  addPupil: (pupil) =>
    set((state) => ({
      pupils: [...state.pupils, pupil],
    })),

  updatePupil: (id, updates) =>
    set((state) => ({
      pupils: state.pupils.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  deletePupil: (id) =>
    set((state) => ({
      pupils: state.pupils.filter((p) => p.id !== id),
    })),

  setPupilSearchQuery: (query) => set({ pupilSearchQuery: query }),

  // General actions
  setSelectedYearGroup: (yearGroup) => set({ selectedYearGroup: yearGroup }),

  // Getters
  getTeacherById: (id) => get().teachers.find((t) => t.id === id),

  getPupilById: (id) => get().pupils.find((p) => p.id === id),

  getParentsByPupilId: (pupilId) =>
    get().parentCarers.filter((p) => p.pupilIds.includes(pupilId)),

  getPupilsByTeacherId: (teacherId) => {
    const state = get();
    const teacherClasses = state.classes.filter((c) => c.teacherId === teacherId);
    const pupilIds = new Set(teacherClasses.flatMap((c) => c.pupilIds));
    return state.pupils.filter((p) => pupilIds.has(p.id));
  },

  getFilteredTeachers: () => {
    const state = get();
    const query = state.teacherSearchQuery.toLowerCase();
    return state.teachers.filter(
      (t) =>
        t.firstName.toLowerCase().includes(query) ||
        t.lastName.toLowerCase().includes(query) ||
        t.email.toLowerCase().includes(query) ||
        t.employeeId.toLowerCase().includes(query)
    );
  },

  getFilteredPupils: () => {
    const state = get();
    const query = state.pupilSearchQuery.toLowerCase();
    let filtered = state.pupils;

    if (state.selectedYearGroup) {
      filtered = filtered.filter((p) => p.yearGroup === state.selectedYearGroup);
    }

    if (query) {
      filtered = filtered.filter(
        (p) =>
          p.firstName.toLowerCase().includes(query) ||
          p.lastName.toLowerCase().includes(query) ||
          p.studentId.toLowerCase().includes(query)
      );
    }

    return filtered;
  },
}));

export default useUserManagementStore;

